import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';

import { FeatureTrackingEvents } from '@revelio/core';
import { Button, Dialog, Input, Link, Text } from '@revelio/ui';

import { copyTextToClipboard } from '../../../../shared/selection-lists/components/SelectionListSelect/utils';
import { CheckIcon } from '../../../../shared/ui/icons';
import { DashMetaRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { createSharedPlot } from '../../api/shared-plots-api';
import { useToast } from '../../hooks/useToast';
import styles from '../Modal/themedModal.module.css';
import { useSharedReportData } from '../SharedReport/SharedReportDataContext';
import { useSafeChart } from './ChartProvider';
import localStyles from './PlotLinkModal.module.css';
import layoutStyles from './plot-link-modal.module.css';

type PlotLinkModalStateType = {
  isPlotLinkOpen: boolean;
  onPlotLinkOpen: () => void;
  onPlotLinkClose: () => void;
};

export interface PlotLinkModalProps {
  plotLinkModalState: PlotLinkModalStateType;
  title: string;
  pageName?: string;
}

export function PlotLinkModal({
  plotLinkModalState,
  title,
  pageName,
}: PlotLinkModalProps) {
  const { isPlotLinkOpen: isOpen, onPlotLinkClose: onClose } =
    plotLinkModalState;

  const sharedReportData = useSharedReportData();
  const { item } = useSafeChart();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [checkData, setCheckData] = useState('');

  // A link from an earlier opening may describe the chart as it was then.
  useEffect(() => {
    if (isOpen) setCheckData('');
  }, [isOpen]);

  const showShareErrorToast = (rateLimited: boolean) => {
    toast({
      position: 'top-right',
      title: rateLimited
        ? 'Too many share links generated'
        : 'Failed to generate share link',
      description: rateLimited
        ? 'Please try again later.'
        : 'Please try again.',
      status: rateLimited ? 'warning' : 'error',
      duration: rateLimited ? 5000 : 3000,
      isClosable: true,
    });
  };

  const createPlotLink = async () => {
    mixpanel.track(FeatureTrackingEvents.PLOT_LINK_GENERATE, {
      plot_title: title,
      // Older events also carry 'legacy'; keeping the property keeps splits
      // by version continuous.
      version: 'v2',
      page: pageName,
      chart_id: item?.id,
    });

    const captured = item
      ? sharedReportData?.getAllChartData()[item.id]
      : undefined;
    if (!captured?.component) {
      console.error('No captured chart data for plot share');
      showShareErrorToast(false);
      return;
    }

    setIsLoading(true);
    try {
      const { token } = await createSharedPlot({
        title,
        component: captured.component,
        props: captured.props ?? {},
      });
      setCheckData(`${window.location.origin}/shared/plot/${token}`);
    } catch (error) {
      console.error('Error generating plot share link:', error);
      showShareErrorToast(error instanceof DashMetaRateLimitError);
    } finally {
      setIsLoading(false);
    }
  };

  const [copiedGeneratedPlotLink, setCopiedGeneratedPlotLink] = useState(false);
  const copyTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutIdRef.current) {
        clearTimeout(copyTimeoutIdRef.current);
      }
    };
  }, []);

  const handleCopyGeneratedPlotLink = async () => {
    const copied = await copyTextToClipboard(checkData);
    if (!copied) return;

    if (copyTimeoutIdRef.current) {
      clearTimeout(copyTimeoutIdRef.current);
    }

    setCopiedGeneratedPlotLink(true);

    copyTimeoutIdRef.current = setTimeout(() => {
      setCopiedGeneratedPlotLink(false);
      copyTimeoutIdRef.current = null;
    }, 2000);
  };
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Shareable plot link"
      className={styles.content}
    >
      <div className={layoutStyles.body}>
        <div className={layoutStyles.linkField}>
          <Text fontSize="sm">Anyone with this link can view this plot</Text>
          <Input
            wrapperClassName={localStyles.linkInput}
            value={checkData}
            placeholder="No shared link"
            disabled={checkData === ''}
            endSlot={
              checkData !== '' ? (
                <Button
                  className={localStyles.copyBtn}
                  size="sm"
                  color="primary"
                  variant="default"
                  onClick={() => void handleCopyGeneratedPlotLink()}
                >
                  {copiedGeneratedPlotLink ? (
                    <>
                      <CheckIcon className={localStyles.copiedIcon} /> Copied
                    </>
                  ) : (
                    'Copy Link'
                  )}
                </Button>
              ) : (
                <Button
                  className={localStyles.copyBtn}
                  size="sm"
                  color="primary"
                  variant="default"
                  loading={isLoading}
                  onClick={createPlotLink}
                >
                  Generate Link
                </Button>
              )
            }
          />
          {checkData !== '' && (
            <div className={layoutStyles.linkAction}>
              <Link
                href={checkData}
                target="_blank"
                rel="noreferrer"
                underline="rest"
                className={localStyles.previewLink}
              >
                Preview link in new tab
              </Link>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
